/**
 * The Cardnd backend behind the `Api` interface (phase 4). Every method calls the real endpoints
 * (see docs/legacy-audit/API.md for how the old site called them) and maps the responses into the
 * domain types with the pure mappers of ./mappers.
 *
 * State kept in memory (per tab): small TTL caches of reference data and vehicle/activity records,
 * the kind of each vehicle id seen (car or bike), the bookings created in this tab (a guest can see
 * the booking they just made) and the reservation a failed payment left behind (never duplicated).
 */
import { addDays, parseISO } from 'date-fns';
import { toISODate } from '@/lib/clock';
import { dailyPrice, PricingError, quoteReservation, type PricingCampaign } from '@/lib/pricing';
import { billableDays } from '@/lib/rental';
import { ApiError, type Api } from '../api';
import { z } from 'zod';
import type {
  Booking,
  Currency,
  ExperienceBookingInput,
  ExperienceSelection,
  RefundTier,
  User,
  VehicleBookingInput,
  VehicleFilters,
  VehicleKind,
  VehicleSearch,
  VehicleSummary,
} from '../domain';
import { getLocale, liveT, ns, translate } from '@/i18n';
import { readSession, writeSession } from '../storage';
import { createHttpClient, MESSAGES, sessionExpiredError, showableServerText, type HttpClient } from './client';
import { clearSession, expireSession, readClient, readToken, saveSession, sessionEnded, signOut } from './session';
import {
  AUTH_MESSAGES,
  authMessage,
  AuthResponseSchema,
  ClientSchema,
  ForgotPasswordSchema,
  ProfileUpdateSchema,
  toUser,
} from './mappers/account';
import {
  ClientRatingsSchema,
  ClientReservationsSchema,
  compactPhone,
  CreateActivityReservationSchema,
  CreateReservationSchema,
  isActivityRow,
  isCartRow,
  LICENCE_AGE,
  OrphanSchema,
  ReservationRowSchema,
  ResumePaymentSchema,
  ServerQuoteSchema,
  toBooking,
  toBookingPayment,
  toCart,
  toQuote,
  toQuoteCharge,
  toTrip,
  utcDay,
  fallbackRef,
  type ReservationRow,
} from './mappers/bookings';
import {
  ActivityCancelSchema,
  CancelPreviewSchema,
  ClientReviewListSchema,
  clientCancelBody,
  ExtensionResponseSchema,
  PartnerStatusSchema,
  toCancelOutcome,
  toCancelPreview,
  toExtension,
  toPartnerStatus,
  toReview,
  VehicleCancelSchema,
} from './mappers/tripActions';
import {
  AGENCY_TYPE,
  BlogListSchema,
  BlogPostSchema,
  contactBody,
  longStayOf,
  partnerLeadForm,
  PlatformConfigSchema,
  RatesSchema,
  toAgency,
  toBlogPost,
  toPlatformConfig,
  type PlatformConfigRecord,
} from './mappers/content';
import {
  ActivityListSchema,
  ActivityQuoteSchema,
  ActivityReservationExtrasSchema,
  ActivitySchema,
  bookingExtras,
  EligibilitySchema,
  isObjectId,
  pickSlot,
  quoteBody,
  SlotListSchema,
  toEligibility,
  toExperience,
  toExperienceDays,
  toExperienceQuote,
  toExperienceSlots,
  toExperienceSummary,
  type ActivityRecord,
  type SlotRecord,
} from './mappers/experiences';
import {
  ConversationListSchema,
  ConversationSchema,
  MessagePageSchema,
  MessageSchema,
  isLiveSupport,
  pendingBotButtons,
  supportDraftThread,
  toMessage,
  toMessages,
  toThread,
  type ConversationRecord,
  type MessageRecord,
} from './mappers/messages';
import { ALL_CATEGORIES, SUPPORT_DRAFT_ID } from '../domain';
import {
  AgencyRulesSchema,
  AvailabilitySchema,
  AvailableBikesSchema,
  AvailableCarsSchema,
  BikesByCitySchema,
  CarsByCitySchema,
  CategoryListSchema,
  idOf,
  PublicAgencySchema,
  toCategorySummaries,
  toCities,
  toCityCounts,
  toDestinations,
  toPricingVehicle,
  toVehicle,
  toVehicleSummary,
  TRANSMISSION_PARAM,
  transmissionLabel,
  VehicleRecordSchema,
  vehicleKindOf,
  type CategoryRecord,
  type PublicAgency,
  type VehicleRecord,
} from './mappers/vehicles';
import { refundTiers } from './mappers/refund';
import {
  COUPON_MESSAGES,
  CouponCheckSchema,
  FinalizeSchema,
  PaymentConfigSchema,
  RESUME_MESSAGES,
  toCouponCheck,
  toFinalizeResult,
  toPaymentConfig,
} from './mappers/payments';
import { metaCookies, newEventId } from '../analytics';

interface ThreadsCache {
  conversations: Map<string, ConversationRecord>;
  messages: Map<string, MessageRecord[]>;
}

export interface HttpApiOptions {
  /** API origin, e.g. `https://api.example.com`; empty = same origin (dev proxy). */
  baseUrl: string;
  /** Build flag of the old site (REACT_APP_ACTIVITIES_ENABLED !== 'false'). */
  activitiesEnabled?: boolean;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
  /** Cars per results page (P15, «Voir plus»); default 20, the server's own default. */
  resultsPageSize?: number;
}

/** Commission rate of the platform (AppParam `commissionRate`, not public): only the guest estimate uses it. */
const DEFAULT_COMMISSION_RATE = 0.15;
const TTL = 60_000;
const LIST_LIMIT = 200;

/** A tiny TTL cache of promises (concurrent callers share one request; failures are not kept). */
function memo<T>(ttl = TTL) {
  const map = new Map<string, { at: number; value: Promise<T> }>();
  return {
    get(key: string, load: () => Promise<T>): Promise<T> {
      const hit = map.get(key);
      if (hit && Date.now() - hit.at < ttl) return hit.value;
      const value = load();
      map.set(key, { at: Date.now(), value });
      value.catch(() => map.delete(key));
      return value;
    },
    set(key: string, value: T) {
      map.set(key, { at: Date.now(), value: Promise.resolve(value) });
    },
    delete: (key: string) => map.delete(key),
    clear: () => map.clear(),
  };
}

const SORT: Record<VehicleFilters['sort'], { sortBy: string; order: 'asc' | 'desc' }> = {
  'price-asc': { sortBy: 'pricePerDay', order: 'asc' },
  'price-desc': { sortBy: 'pricePerDay', order: 'desc' },
  'deposit-asc': { sortBy: 'deposit', order: 'asc' },
};

const notFound = (message: string) => new ApiError(message, 'not_found', { status: 404 });

/** User-facing messages of this file (src/i18n/errors), read in the current language when the error is raised. */
const tx = liveT(ns.errors);

export function createHttpApi(options: HttpApiOptions): Api {
  const baseUrl = options.baseUrl.replace(/\/$/, '');
  const activitiesBuildFlag = options.activitiesEnabled ?? true;
  const resultsPageSize = options.resultsPageSize && options.resultsPageSize > 0 ? options.resultsPageSize : 20;
  const http: HttpClient = createHttpClient({
    baseUrl,
    getToken: readToken,
    // CK-02: once the session expired, an authed call is refused instead of going out as a guest's.
    sessionEnded,
    // The old site's interceptor: a refused token means the session is over (AU-02): cleared, the shell redirects.
    onUnauthorized: expireSession,
    timeoutMs: options.timeoutMs,
    fetchImpl: options.fetchImpl,
  });

  /* ── Caches ─────────────────────────────────────────────────────────── */
  const configRaw = memo<PlatformConfigRecord>();
  const categories = memo<CategoryRecord[]>(5 * TTL);
  const vehicleRaw = memo<VehicleRecord>();
  const cityVehicles = memo<VehicleRecord[]>();
  const agencies = memo<PublicAgency | null>(5 * TTL);
  const activities = memo<ActivityRecord>();
  const activityLists = memo<ActivityRecord[]>();
  const slots = memo<SlotRecord[]>(30_000);
  const kinds = new Map<string, VehicleKind>();
  const activityIds = new Set<string>();
  const created = new Map<string, Booking>();
  /**
   * The vehicle reservation of this tab whose payment is not done (F7/F8): its booking key, the Meta event
   * id it was created with and, once known, its intent. Same booking again → resume it; another → abandon it.
   */
  let pendingPayment: {
    key: string;
    reservationId: string;
    eventId?: string;
    paymentIntentId?: string;
    booking?: Booking;
  } | null = null;
  let threadsCache: ThreadsCache = { conversations: new Map(), messages: new Map() };
  const rowKinds = new Map<string, 'vehicle' | 'experience'>();
  const rowVehicle = new Map<string, string>();

  const getConfigRaw = () =>
    configRaw.get('platform', () => http.get('/api/config/platform', { auth: 'none', schema: PlatformConfigSchema }));

  const categoryNames = async () => {
    const list = await getCategories().catch(() => [] as CategoryRecord[]);
    const map = new Map<string, string>();
    for (const c of list) {
      map.set(c._id, c.name);
      if (c.slug) map.set(c.slug, c.name);
    }
    return map;
  };
  const getCategories = () =>
    categories.get('all', () => http.get('/api/categories', { auth: 'none', schema: CategoryListSchema }));

  const remember = (raw: VehicleRecord) => {
    kinds.set(raw._id, vehicleKindOf(raw));
    return raw;
  };

  /** A vehicle document: the endpoint is chosen from the kind already seen for this id (cars first otherwise). */
  const getVehicleRaw = (id: string): Promise<VehicleRecord> => {
    if (!isObjectId(id)) return Promise.reject(notFound(tx('vehicleGone')));
    return vehicleRaw.get(id, async () => {
      const known = kinds.get(id);
      const order: VehicleKind[] = known ? [known] : ['car', 'moto'];
      for (const kind of order) {
        try {
          const raw = await http.get(`/api/client/${kind === 'moto' ? 'bikes' : 'cars'}/${id}`, {
            auth: 'none',
            schema: VehicleRecordSchema,
          });
          return remember(raw);
        } catch (e) {
          if (!(e instanceof ApiError && e.code === 'not_found')) throw e;
        }
      }
      throw notFound(tx('vehicleGone'));
    });
  };

  const getAgency = (id: string): Promise<PublicAgency | null> =>
    id
      ? agencies.get(id, () =>
          http.get(`/api/client/agencies/${id}`, { auth: 'none', schema: PublicAgencySchema }).catch((e: unknown) => {
            if (e instanceof ApiError && e.code === 'not_found') return null;
            throw e;
          }),
        )
      : Promise.resolve(null);

  /** Vehicles of a city (no dates): one card per model, as the storefront lists them. */
  const getCityVehicles = (kind: VehicleKind, city: string): Promise<VehicleRecord[]> =>
    cityVehicles.get(`${kind}|${city}`, async () => {
      const list =
        kind === 'moto'
          ? (
              await http.get('/api/client/bikes/available', {
                auth: 'none',
                query: { city },
                schema: AvailableBikesSchema,
              })
            ).bikes
          : (
              await http.get('/api/client/available-cars', {
                auth: 'none',
                query: { city, limit: LIST_LIMIT },
                schema: AvailableCarsSchema,
              })
            ).cars;
      return list.map(remember);
    });

  const getActivity = (id: string): Promise<ActivityRecord> =>
    activities.get(id, async () => {
      const path = isObjectId(id)
        ? `/api/client/activities/${id}`
        : `/api/client/activities/public/${encodeURIComponent(id)}`;
      const a = await http.get(path, {
        auth: 'none',
        schema: ActivitySchema,
        messages: { not_found: tx('experienceGone') },
      });
      activityIds.add(a._id);
      activities.set(a._id, a);
      return a;
    });

  const listActivities = (city: string) =>
    activityLists.get(city, async () => {
      const list = await http.get('/api/client/activities', {
        auth: 'none',
        query: { city: city || undefined },
        schema: ActivityListSchema,
      });
      for (const a of list) {
        activityIds.add(a._id);
        activities.set(a._id, a);
      }
      return list;
    });

  /** Open departures of an activity around `from` (slot dates are Moroccan midnights). */
  const getSlots = (activityId: string, from: string, days: number) =>
    slots.get(`${activityId}|${from}|${days}`, () =>
      http.get(`/api/client/activities/${activityId}/slots`, {
        auth: 'none',
        query: {
          from: toISODate(addDays(parseISO(from), -1)),
          to: toISODate(addDays(parseISO(from), days + 1)),
        },
        schema: SlotListSchema,
      }),
    );

  /** The departure a selection books (day, time, option), refused when none is left for the party. */
  async function slotFor(a: ActivityRecord, sel: ExperienceSelection): Promise<SlotRecord> {
    const daySlots = await getSlots(a._id, sel.date, 1);
    const seats = sel.adults + sel.children;
    const time = sel.time || undefined;
    const slot = pickSlot(daySlots, sel.date, sel.packId, seats, time);
    if (slot) return slot;
    throw new ApiError(
      pickSlot(daySlots, sel.date, sel.packId, 1, time)
        ? time
          ? tx('slot.noSeatsAt', { time, count: seats })
          : tx('slot.noSeatsDay', { count: seats })
        : time && pickSlot(daySlots, sel.date, sel.packId, seats)
          ? tx('slot.noDepartureAt', { time })
          : tx('slot.noDeparture'),
      'invalid',
    );
  }

  /**
   * F8 for experiences: the unpaid activity reservation of this tab, by checkout input. The same input again
   * resumes its payment (`POST /api/payments/activity-reservations/:id/intent`, the server reuses a live intent)
   * instead of booking twice; another input books anew and leaves the unpaid one to the server's 30-minute sweep
   * (there is no client abandon endpoint for activities).
   */
  let pendingExperience: { key: string; reservationId: string; booking?: Booking } | null = null;
  const experienceKey = (i: ExperienceBookingInput) =>
    JSON.stringify([
      i.selection.experienceId,
      i.selection.packId,
      i.selection.date,
      i.selection.time ?? '',
      i.selection.adults,
      i.selection.children,
      i.selection.infants ?? 0,
      [...i.selection.addonIds].sort(),
      i.selection.pickup ?? null,
      i.selection.grantId ?? '',
      i.currency,
      i.contact,
      i.answers,
      i.ages ?? [],
      i.heightsCm ?? [],
      i.weightsKg ?? [],
      i.vehicleAllocation ?? null,
    ]);

  const paymentMode = async (): Promise<'full' | 'commission_only'> =>
    (await getConfigRaw()).onlinePaymentMode === 'commission_only' ? 'commission_only' : 'full';

  /** Reservations of the signed-in client (vehicles and experiences), each one's kind remembered. */
  const getReservations = async (): Promise<ReservationRow[]> => {
    const res = await http.get('/api/stats/client/reservations', {
      auth: 'required',
      schema: ClientReservationsSchema,
      messages: { unauthorized: tx('tripsSignIn') },
    });
    for (const r of res.data) {
      rowKinds.set(r._id, isActivityRow(r) ? 'experience' : 'vehicle');
      const v = r.car ?? r.bike;
      if (v) rowVehicle.set(r._id, idOf(v));
    }
    return res.data.filter((r) => !r.clientHiddenAt);
  };

  const agencyNames = async (rows: ReservationRow[]) => {
    const ids = [...new Set(rows.map((r) => r.confirmedAgencyId).filter((x): x is string => !!x))];
    const list = await Promise.all(ids.map((id) => getAgency(id).catch(() => null)));
    return new Map(
      ids.map((id, i) => [id, list[i]?.agencyName ?? '']).filter(([, nme]) => !!nme) as [string, string][],
    );
  };

  const user = (): User | null => {
    const c = readClient();
    return c && readToken() ? toUser(ClientSchema.parse(c)) : null;
  };

  /**
   * CK-02: before a booking made in the user's name. A stored token that has expired ends the session here
   * (cleared, the shell sends the user to sign in) and the booking is refused: never sent as a guest's.
   */
  const assertSession = () => {
    readToken();
    if (sessionEnded()) throw sessionExpiredError();
  };

  /* ── Vehicles helpers ───────────────────────────────────────────────── */

  /** The search with the platform blackout flag (API-VEH-5: `blackout: true` and no vehicle on these dates). */
  async function searchVehicles(
    search: VehicleSearch,
    filters: VehicleFilters,
  ): Promise<{ items: VehicleSummary[]; blackout: boolean }> {
    const { sortBy, order } = SORT[filters.sort] ?? SORT['price-asc'];
    const dates = {
      startDate: search.start,
      returnDate: search.end,
      pickupTime: search.pickupTime,
      returnTime: search.returnTime,
    };
    const names = await categoryNames();
    let records: VehicleRecord[];
    let blackout: boolean;
    if (search.kind === 'moto') {
      const res = await http.get('/api/client/bikes/available', {
        auth: 'none',
        query: { city: search.city, ...dates, sortBy, order },
        schema: AvailableBikesSchema,
      });
      records = res.bikes;
      blackout = res.blackout === true;
    } else {
      const res = await http.get('/api/client/available-cars', {
        auth: 'none',
        query: {
          city: search.city,
          ...dates,
          sortBy,
          order,
          limit: LIST_LIMIT,
          category: filters.category !== ALL_CATEGORIES ? filters.category : undefined,
          transmission: filters.transmission !== 'all' ? TRANSMISSION_PARAM[filters.transmission] : undefined,
          // The backend reads `freeDelivery` (the old grid sent `delivery`: F-06).
          freeDelivery: filters.freeDelivery ? 'true' : undefined,
        },
        schema: AvailableCarsSchema,
      });
      records = res.cars;
      blackout = res.blackout === true;
    }
    // Sorted on the daily price the card shows (billable days, the bike step that applies: PR-07).
    const days = billableDays(search.start, search.end, search.pickupTime, search.returnTime);
    let list = records
      .map(remember)
      .map((r) => toVehicleSummary(r, baseUrl, names))
      .filter((v) => filters.category === ALL_CATEGORIES || v.category === filters.category)
      .filter(
        (v) =>
          filters.transmission === 'all' ||
          v.transmission.startsWith(transmissionLabel(TRANSMISSION_PARAM[filters.transmission])),
      )
      .filter((v) => !filters.freeDelivery || v.freeDelivery);
    if (filters.sort === 'price-asc') list = [...list].sort((a, b) => dailyPrice(a, days) - dailyPrice(b, days));
    if (filters.sort === 'price-desc') list = [...list].sort((a, b) => dailyPrice(b, days) - dailyPrice(a, days));
    if (filters.sort === 'deposit-asc') list = [...list].sort((a, b) => a.deposit - b.deposit);
    return { items: list, blackout };
  }

  /** The campaign the backend would apply on the start date (listing payloads carry the one active today). */
  function campaignFor(raw: VehicleRecord, start: string): { campaign: PricingCampaign | null; pricePerDay: number } {
    const price = Number(raw.pricePerDay) || 0;
    if (raw.pricingSource !== 'campaign' || !raw.campaignStartDate || !raw.campaignEndDate) {
      return { campaign: null, pricePerDay: price };
    }
    const from = utcDay(raw.campaignStartDate);
    const to = utcDay(raw.campaignEndDate);
    if (start >= from && start < to) {
      return { campaign: { pricePerDay: price, pricingTiers: raw.pricingTiers ?? undefined }, pricePerDay: price };
    }
    return { campaign: null, pricePerDay: Number(raw.originalPricePerDay) || price };
  }

  async function vehicleQuote(vehicleId: string, search: VehicleSearch, currency: Currency = 'MAD') {
    const [raw, cfg] = await Promise.all([getVehicleRaw(vehicleId), getConfigRaw()]);
    const kind = vehicleKindOf(raw);
    const mode = cfg.onlinePaymentMode === 'commission_only' ? 'commission_only' : 'full';
    const longStay = longStayOf(cfg);
    const agency = await getAgency(idOf(raw.agencyId)).catch(() => null);
    const freeDelivery =
      agency?.freeDelivery === true ||
      ((agency?.deliveryEnabled === true || agency?.deliveryEnabled === 'true') && !Number(agency?.deliveryPrice));
    const common = {
      pickupTime: search.pickupTime,
      returnTime: search.returnTime,
      delivery: search.delivery,
      freeDelivery,
      deposit: Number(raw.deposit) || 0,
      paymentMode: mode,
      longStay,
    } as const;
    const body = {
      ...(kind === 'moto' ? { bike: vehicleId, vehicleType: 'bike' } : { car: vehicleId, vehicleType: 'car' }),
      startDate: search.start,
      returnDate: search.end,
      pickupTime: search.pickupTime,
      returnTime: search.returnTime,
      rentalType: search.delivery ? 'delivery' : 'pickup',
      // PR-11 (Decision 45): the server adds the online charge in this currency, at the PaymentIntent's rate.
      currency,
    };
    if (readToken()) {
      try {
        const q = await http.post('/api/reservations/quote', {
          auth: 'required',
          json: body,
          schema: ServerQuoteSchema,
          messages: { 404: tx('vehicleGone') },
        });
        const converted = toQuoteCharge(q.converted);
        return toQuote(
          {
            days: q.nights,
            totalPrice: q.totalPrice,
            platformFee: q.platformFee,
            deliveryFee: Number(q.deliveryFee) || 0,
            // Only the currency asked for (a conversion failure leaves MAD only).
            converted: converted?.currency === currency ? converted : undefined,
          },
          { ...common, isEstimate: false },
        );
      } catch (e) {
        if (e instanceof ApiError && e.details.status === 400) throw quoteRefusal(e, raw);
        // An expired session falls back to the guest estimate below.
        if (!(e instanceof ApiError && e.code === 'unauthorized')) throw e;
      }
    }
    const { campaign, pricePerDay } = campaignFor(raw, search.start);
    try {
      const q = quoteReservation(
        { ...toPricingVehicle(raw), pricePerDay },
        {
          commissionRate: DEFAULT_COMMISSION_RATE,
          longStay,
          platformMinRentalDays: Number(cfg.minRentalDays) || 1,
          campaign,
          agencyDelivery: agency
            ? {
                enabled: agency.deliveryEnabled === true || agency.deliveryEnabled === 'true',
                price: Number(agency.deliveryPrice) || 0,
              }
            : { enabled: raw.deliveryEnabled === true, price: Number(raw.deliveryPrice) || 0 },
        },
        {
          startDate: search.start,
          returnDate: search.end,
          pickupTime: search.pickupTime,
          returnTime: search.returnTime,
          rentalType: search.delivery ? 'delivery' : 'pickup',
        },
      );
      return toQuote(
        { days: q.nights, totalPrice: q.totalPrice, platformFee: q.platformFee, deliveryFee: q.deliveryFee },
        { ...common, isEstimate: true },
      );
    } catch (e) {
      if (e instanceof PricingError) throw pricingRefusal(e);
      throw e;
    }
  }

  const minDaysMessage = (n: number) => tx('minDays', { count: n });
  const pricingRefusal = (e: PricingError) =>
    new ApiError(
      e.code === 'min_days' && e.minRentalDays
        ? minDaysMessage(e.minRentalDays)
        : e.code === 'invalid_dates'
          ? tx('returnBeforeStart')
          : MESSAGES.invalid,
      'invalid',
      { status: 400, serverCode: e.code },
    );
  function quoteRefusal(e: ApiError, raw: VehicleRecord) {
    const min = Number(raw.minRentalDays) || 0;
    return /minimum/i.test(e.message) || e.details.serverCode === 'min_days'
      ? new ApiError(minDaysMessage(min || 1), 'invalid', e.details)
      : e;
  }

  /* ── Bookings helpers ───────────────────────────────────────────────── */

  const bookingKey = (i: VehicleBookingInput) =>
    JSON.stringify([
      i.vehicleId,
      i.search.start,
      i.search.end,
      i.search.pickupTime,
      i.search.returnTime,
      i.delivery.enabled ? i.delivery.address.trim() : '',
      (i.coupon ?? '').trim().toUpperCase(),
      i.currency ?? 'MAD',
    ]);

  function vehicleBookingForm(input: VehicleBookingInput, kind: VehicleKind, total: number | undefined): FormData {
    const f = new FormData();
    const d = input.driver;
    // Field order of the old checkout (PAY-08).
    if (kind === 'moto') {
      f.append('bike', input.vehicleId);
      f.append('vehicleType', 'bike');
    } else {
      f.append('car', input.vehicleId);
      f.append('vehicleType', 'car');
    }
    f.append('client', user()?.id ?? '');
    f.append('rentalType', input.delivery.enabled ? 'delivery' : 'pickup');
    f.append('sameReturnLocation', 'true');
    // Local calendar dates as typed: no UTC shift (F3).
    f.append('startDate', input.search.start);
    f.append('returnDate', input.search.end);
    if (total !== undefined) f.append('totalPrice', total.toFixed(2)); // informative: the server reprices
    if (input.delivery.enabled && input.delivery.address.trim())
      f.append('deliveryLocation', input.delivery.address.trim());
    f.append('returnLocation', '');
    f.append('pickupTime', input.search.pickupTime);
    f.append('returnTime', input.search.returnTime);
    f.append('firstName', d.firstName);
    f.append('lastName', d.lastName);
    f.append('country', d.country?.trim() || 'Maroc');
    f.append('city', input.search.city);
    f.append('email', d.email);
    f.append('phoneNumber', compactPhone(d.phone));
    f.append('driverLicenceAge', LICENCE_AGE[d.licenceYears]);
    f.append('termsAccepted', 'true');
    f.append('paymentMethod', 'online_payment');
    // CUR-01: the card is charged in the chosen currency, converted by the server at its own rate.
    f.append('currency', input.currency ?? 'MAD');
    if (input.coupon?.trim()) f.append('couponCode', input.coupon.trim().toUpperCase());
    return f;
  }

  /** The Meta headers of a reservation (F-07): the event id the Purchase pixel reuses, and the cookies. */
  const metaHeaders = (eventId: string): Record<string, string> => {
    const { fbp, fbc } = metaCookies();
    return { 'X-Meta-Event-Id': eventId, 'X-Meta-FBP': fbp ?? '', 'X-Meta-FBC': fbc ?? '' };
  };

  /** A 502/503 after the reservation was written: keep its id so a retry resumes it instead of creating another. */
  function paymentFailure(e: ApiError, body: unknown, key?: string): ApiError {
    const orphan = OrphanSchema.safeParse(body);
    const reservationId = orphan.success ? (orphan.data.data?._id ?? orphan.data.reservation?._id) : undefined;
    if (reservationId && key) pendingPayment = { key, reservationId };
    const message =
      e.details.status === 503 || e.details.serverCode === 'RATE_UNAVAILABLE'
        ? tx('resume.RATE_UNAVAILABLE')
        : tx('booking.paymentInit');
    return new ApiError(message, 'unavailable', { ...e.details, reservationId });
  }

  const bookingErrorMessages = {
    400: tx('booking.invalid'),
    404: tx('vehicleGone'),
    409: tx('booking.taken'),
  } as const;

  function bookingError(status: number, body: unknown): ApiError {
    const b = (body ?? {}) as { message?: unknown; code?: unknown; minRentalDays?: unknown };
    const serverCode = typeof b.code === 'string' ? b.code : undefined;
    const msg = typeof b.message === 'string' ? b.message : '';
    if (status === 401 || status === 403)
      return new ApiError(MESSAGES.unauthorized, 'unauthorized', { status, serverCode });
    if (typeof b.minRentalDays === 'number')
      return new ApiError(minDaysMessage(b.minRentalDays), 'invalid', { status, serverCode });
    if (/licen[cs]e/i.test(msg))
      return new ApiError(tx('booking.licence'), 'invalid', { status });
    if (/returnDate must be after/i.test(msg)) return new ApiError(tx('returnBeforeStart'), 'invalid', { status });
    // i18n-ignore-next-line: detection of a French server message (shown as is in French only)
    const french = getLocale() === 'fr' && msg && /[éèàùçêô’']|\b(la|le|les|ce|cette|merci)\b/i.test(msg) ? msg : undefined;
    const code = status === 404 ? 'not_found' : status === 429 || status >= 500 ? 'unavailable' : 'invalid';
    return new ApiError(french ?? (bookingErrorMessages as Record<number, string>)[status] ?? MESSAGES[code], code, {
      status,
      serverCode,
    });
  }

  const fromBooking = async (row: ReservationRow, extra: Partial<Parameters<typeof toBooking>[1]> = {}) =>
    toBooking(row, { baseUrl, paymentMode: await paymentMode(), ...extra });

  /* ── Experience booking helpers (P6) ── */

  /** `GET /api/client/activity-reservations/:id`: the row plus its voucher QR, tickets and infants. */
  const getActivityReservation = async (id: string) => {
    const raw = await http.get(`/api/client/activity-reservations/${id}`, {
      auth: 'required',
      messages: { 404: tx('bookingNotFound'), 403: tx('bookingNotFound') },
    });
    const row = ReservationRowSchema.safeParse(raw);
    const extras = ActivityReservationExtrasSchema.safeParse(raw);
    if (!row.success || !extras.success)
      throw new ApiError(MESSAGES.badResponse, 'unavailable', { serverCode: 'BAD_RESPONSE' });
    return { ...row.data, extras: extras.data };
  };

  /**
   * Re-opens the payment of this tab's unpaid activity reservation. Still unpaid → `POST
   * /api/payments/activity-reservations/:id/intent` (the server reuses a live intent in the same currency);
   * already authorised or paid → reported as paid (never a second authorisation); gone → null (book again).
   */
  async function resumeExperience(
    id: string,
    currency: string,
    asRow: (r: ReservationRow) => ReservationRow,
  ): Promise<Booking | null> {
    const r = await getActivityReservation(id);
    const paid = ['requires_capture', 'succeeded'].includes(r.payment?.status ?? '');
    if (r.status !== 'awaiting_payment' && !paid) return null;
    let payment = { zeroFee: true } as Booking['payment'];
    if (!paid) {
      try {
        payment = toBookingPayment(
          await http.post(`/api/payments/activity-reservations/${id}/intent`, {
            auth: 'required',
            json: { currency },
            schema: ResumePaymentSchema,
          }),
        );
      } catch (e) {
        if (e instanceof ApiError && (e.details.status === 502 || e.details.status === 503))
          throw new ApiError(paymentFailure(e, null).message, 'unavailable', { ...e.details, reservationId: id });
        if (e instanceof ApiError && e.details.status === 409) payment = { zeroFee: true };
        else throw e;
      }
    }
    const booking = await fromBooking(asRow(r), { agencyName: translate(ns.labels, 'verifiedProvider'), payment });
    booking.experienceId = typeof r.activity === 'string' ? r.activity : (r.activity?._id ?? undefined);
    created.set(booking.id, booking);
    rowKinds.set(booking.id, 'experience');
    return booking;
  }

  const experienceErrorMessages = {
    400: tx('experienceBooking.invalid'),
    404: tx('experienceBooking.slotGone'),
    409: tx('experienceBooking.slotTaken'),
    422: tx('experienceBooking.details'),
  } as const;

  /** The server's French reason when it gives one and the interface is French (cut-off, pickup, requirements), else ours. */
  function experienceBookingError(status: number, body: unknown): ApiError {
    const b = (body ?? {}) as { message?: unknown; code?: unknown; reason?: unknown };
    const msg = typeof b.message === 'string' ? b.message : '';
    const serverCode =
      typeof b.code === 'string' ? b.code : typeof b.reason === 'string' ? b.reason : undefined;
    if (status === 401 || status === 403)
      return new ApiError(MESSAGES.unauthorized, 'unauthorized', { status, serverCode });
    if (/waiver/i.test(msg))
      return new ApiError(tx('experienceBooking.waiver'), 'invalid', { status, serverCode });
    const code = status === 404 ? 'not_found' : status === 429 || status >= 500 ? 'unavailable' : 'invalid';
    const french = showableServerText(msg);
    return new ApiError(
      french ?? (experienceErrorMessages as Record<number, string>)[status] ?? MESSAGES[code],
      code,
      { status, serverCode },
    );
  }

  /* ── Messages helpers ───────────────────────────────────────────────── */

  /** A page of 50 messages (the latest, or those before `before`), oldest first. */
  const getMessagePage = (id: string, before?: string) =>
    http.get(`/api/conversations/${id}/messages`, {
      auth: 'required',
      query: { limit: 50, before },
      schema: MessagePageSchema,
    });
  const getMessages = async (id: string): Promise<MessageRecord[]> => (await getMessagePage(id)).messages;

  const getConversation = (id: string) =>
    http.get(`/api/conversations/${id}`, { auth: 'required', schema: ConversationSchema });

  const botClick = (id: string, buttonId: string) =>
    http.post(`/api/conversations/${id}/bot-response`, { auth: 'required', json: { buttonId } });

  /**
   * The support bot accepts only its buttons while it runs. Typing a message means «I want a person»:
   * walk the bot's own path to an agent (`Parler à un agent`, `Autre sujet`, `Autres sujets`, `Non…`).
   */
  async function handOffToAgent(id: string): Promise<void> {
    const PATH = ['talk_to_agent', 'other', 'back_to_topics', 'no'];
    for (let step = 0; step < 4; step++) {
      const conv = await getConversation(id);
      if (conv.status !== 'bot_flow') return;
      const buttons = pendingBotButtons(conv, await getMessages(id));
      const next = PATH.map((p) => buttons.find((x) => x.id === p)).find(Boolean);
      if (!next) break;
      await botClick(id, next.id);
    }
    const conv = await getConversation(id);
    if (conv.status === 'bot_flow') {
      throw new ApiError(tx('messages.botChoice'), 'invalid');
    }
  }

  /* ── The Api ────────────────────────────────────────────────────────── */

  return {
    config: {
      get: async () => {
        const [cfg, rates] = await Promise.all([
          getConfigRaw(),
          http.get('/api/currency/rates', { auth: 'none', schema: RatesSchema }).catch(() => null),
        ]);
        return toPlatformConfig(cfg, rates, activitiesBuildFlag);
      },
    },

    cities: {
      vehicles: async (kind) => {
        if (kind === 'moto') {
          const res = await http.get('/api/client/bikes-by-city', { auth: 'none', schema: BikesByCitySchema });
          return toCities(toCityCounts(res.citiesWithBikes.map((c) => ({ _id: c._id, items: c.bikes }))));
        }
        const res = await http.get('/api/client/cars-by-city', { auth: 'none', schema: CarsByCitySchema });
        return toCities(toCityCounts(res.citiesWithCars.map((c) => ({ _id: c._id, items: c.cars }))));
      },
      experiences: async () => {
        const list = await listActivities('').catch((e: unknown) => {
          if (e instanceof ApiError && e.code === 'not_found') return [] as ActivityRecord[]; // activities gate off
          throw e;
        });
        const counts = new Map<string, number>();
        for (const a of list) if (a.city) counts.set(a.city, (counts.get(a.city) ?? 0) + 1);
        return [...counts.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([city, count]) => ({
            id: city,
            name: city,
            caption: translate(ns.labels, 'city.experiences', { count }),
          }));
      },
      destinations: async () => {
        const res = await http.get('/api/client/cars-by-city', { auth: 'none', schema: CarsByCitySchema });
        return toDestinations(toCityCounts(res.citiesWithCars.map((c) => ({ _id: c._id, items: c.cars }))));
      },
    },

    vehicles: {
      search: async (search, filters) => (await searchVehicles(search, filters)).items,
      categories: async (kind, city) => {
        const [cats, records] = await Promise.all([getCategories(), getCityVehicles(kind, city)]);
        const names = await categoryNames();
        const type = kind === 'moto' ? 'bikes' : 'cars';
        return toCategorySummaries(
          cats.filter((c) => (c.type ?? 'cars') === type),
          records.map((r) => toVehicleSummary(r, baseUrl, names)),
          baseUrl,
        );
      },
      featured: async (city) => {
        const names = await categoryNames();
        return (await getCityVehicles('car', city)).slice(0, 8).map((r) => toVehicleSummary(r, baseUrl, names));
      },
      get: async (id) => {
        const raw = await getVehicleRaw(id);
        const agencyId = idOf(raw.agencyId);
        const [agency, rules, names] = await Promise.all([
          getAgency(agencyId).catch(() => null),
          agencyId
            ? http
                .get(`/api/agency-rules/public/${agencyId}`, { auth: 'none', schema: AgencyRulesSchema })
                .catch(() => [])
            : Promise.resolve([]),
          categoryNames(),
        ]);
        return toVehicle(raw, baseUrl, agency, rules, names);
      },
      similar: async (id) => {
        const raw = await getVehicleRaw(id);
        const kind = vehicleKindOf(raw);
        const names = await categoryNames();
        const price = Number(raw.pricePerDay) || 0;
        return (await getCityVehicles(kind, raw.city ?? ''))
          .filter((r) => r._id !== id)
          .map((r) => toVehicleSummary(r, baseUrl, names))
          .sort((a, b) => Math.abs(a.pricePerDay - price) - Math.abs(b.pricePerDay - price))
          .slice(0, 4);
      },
      quote: vehicleQuote,
      availability: async (vehicleId, start, end, pickupTime, returnTime) => {
        const raw = await getVehicleRaw(vehicleId);
        const res = await http.get(
          `/api/client/${vehicleKindOf(raw) === 'moto' ? 'bikes' : 'cars'}/${vehicleId}/check-availability`,
          {
            auth: 'none',
            // Local calendar dates and wall times, exactly as typed (AV-01; no toISOString shift: F3).
            query: { startDate: start, returnDate: end, pickupTime, returnTime },
            schema: AvailabilitySchema,
            messages: { 400: tx('returnBeforeStart') },
          },
        );
        // Only `available === true` counts (H20).
        return res.blackout === true ? { available: false, blackout: true } : { available: res.available === true };
      },
      refundPolicy: async (start, pickupTime): Promise<RefundTier[]> => refundTiers(start, pickupTime, new Date()),
    },

    agencies: {
      get: async (slug) => {
        // The public route is the agency's cars by exact name (`/api/agency/:agencyName`); the agency
        // profile by name is staff-only (F-03), so it is read from its id.
        const cars = await http.get(`/api/agency/${encodeURIComponent(slug)}`, {
          auth: 'none',
          schema: z.array(VehicleRecordSchema),
          messages: { not_found: tx('agencyNotFound') },
        });
        const agencyId = idOf(cars[0]?.agencyId);
        const agency = await getAgency(agencyId);
        if (!agency) throw notFound(tx('agencyNotFound'));
        const names = await categoryNames();
        const seen = new Set<string>();
        const models = cars
          .filter((c) => c.status !== 'Maintenance')
          .filter((c) => {
            const key = `${String(c.brand)}|${String(c.subBrand)}|${c.city ?? ''}`;
            return !seen.has(key) && seen.add(key);
          })
          .map(remember)
          .map((c) => toVehicleSummary(c, baseUrl, names));
        return toAgency(slug, agency, models);
      },
    },

    blog: {
      // `/api/blogs` is not mounted by the backend today (F-05): an empty list, never a crash.
      list: async () => {
        try {
          const posts = await http.get('/api/blogs', { auth: 'none', schema: BlogListSchema });
          return posts.map((p) => {
            const { paragraphs: _p, ...summary } = toBlogPost(p, baseUrl);
            return summary;
          });
        } catch (e) {
          if (e instanceof ApiError && e.code === 'not_found') return [];
          throw e;
        }
      },
      get: async (id) => {
        const res = await http.get(`/api/blogs/${encodeURIComponent(id)}`, {
          auth: 'none',
          schema: BlogPostSchema,
          messages: { not_found: tx('articleNotFound') },
        });
        const post = 'post' in res && res.post && typeof res.post === 'object' ? (res.post as typeof res) : res;
        return toBlogPost(post as Parameters<typeof toBlogPost>[0], baseUrl);
      },
    },

    experiences: {
      list: async (f) => {
        const list = await listActivities(f.city);
        const mins = (m: number) =>
          !f.duration
            ? true
            : f.duration === 'court'
              ? m <= 60
              : f.duration === 'moyen'
                ? m > 60 && m <= 180
                : f.duration === 'long'
                  ? m > 180 && m <= 360
                  : m > 360;
        return list
          .map((a) => toExperienceSummary(a, baseUrl))
          .filter((e) => (f.category === ALL_CATEGORIES || e.category === f.category) && mins(e.durationMinutes));
      },
      categories: async (city) => {
        const counts = new Map<string, number>();
        for (const a of await listActivities(city)) {
          const label = toExperienceSummary(a, baseUrl).category;
          if (label) counts.set(label, (counts.get(label) ?? 0) + 1);
        }
        return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([label, count]) => ({ label, count }));
      },
      get: async (id) => toExperience(await getActivity(id), baseUrl),
      days: async (id, from) => {
        const a = await getActivity(id);
        return toExperienceDays(await getSlots(a._id, from, 5), from, 5);
      },
      quote: async (sel) => {
        const a = await getActivity(sel.experienceId);
        const slot = await slotFor(a, sel);
        // With a free-entry grant the server reads the client (the grant is checked against the account, AC-01).
        const q = await http.post(`/api/client/activities/${a._id}/quote`, {
          auth: sel.grantId ? 'required' : 'none',
          json: quoteBody(a, sel, slot),
          schema: ActivityQuoteSchema,
          messages: { 404: tx('experienceBooking.slotGone') },
        });
        // Experiences are always paid in full online, whatever the platform mode (backend `paymentMethod`).
        return { ...toExperienceQuote(q, 'full'), time: slot.startTime ?? undefined };
      },
      slots: async (id, from, to) => {
        const a = await getActivity(id);
        const days = Math.max(1, Math.round((parseISO(to).getTime() - parseISO(from).getTime()) / 86_400_000) + 1);
        // The request is padded by a day on each side: keep the asked days only.
        return toExperienceSlots(await getSlots(a._id, from, days)).filter((x) => x.date >= from && x.date <= to);
      },
    },

    bookings: {
      createVehicle: async (input) => {
        assertSession();
        const raw = await getVehicleRaw(input.vehicleId);
        const kind = vehicleKindOf(raw);
        const key = bookingKey(input);
        const summary = toVehicleSummary(raw, baseUrl, await categoryNames());
        const vehicle = {
          id: raw._id,
          name: summary.name,
          image: summary.image,
          city: input.search.city,
          year: summary.year,
        };

        // F7/F8: this tab already holds an unpaid reservation (payment not started, or card step closed).
        // Same booking → resume it (the server reuses the intent while Stripe accepts it); another booking →
        // release the unpaid one first (fire and forget), as the old checkout did.
        const prev = pendingPayment;
        if (prev && prev.key === key && !readToken() && prev.booking) return prev.booking;
        if (prev && prev.key === key && readToken()) {
          try {
            const pay = await http.patch(`/api/reservations/${prev.reservationId}/resume-payment`, {
              auth: 'required',
              json: { currency: input.currency ?? 'MAD' },
              schema: ResumePaymentSchema,
            });
            const payment = toBookingPayment(pay);
            const rows = await getReservations().catch(() => [] as ReservationRow[]);
            const row = rows.find((r) => r._id === prev.reservationId);
            const booking = row
              ? await fromBooking(row, { vehicle, payment })
              : prev.booking && { ...prev.booking, payment };
            if (booking) {
              booking.metaEventId = prev.eventId;
              created.set(booking.id, booking);
              pendingPayment = payment?.zeroFee
                ? null
                : { ...prev, paymentIntentId: payment?.paymentIntentId, booking };
              return booking;
            }
            pendingPayment = null;
          } catch (e) {
            if (e instanceof ApiError && (e.details.status === 502 || e.details.status === 503)) {
              throw new ApiError(paymentFailure(e, null).message, 'unavailable', {
                ...e.details,
                reservationId: prev.reservationId,
              });
            }
            pendingPayment = null;
            // Already authorised: never book the same stay twice.
            if (e instanceof ApiError && e.details.serverCode === 'ALREADY_PAID') {
              throw new ApiError(RESUME_MESSAGES.ALREADY_PAID ?? MESSAGES.invalid, 'invalid', {
                ...e.details,
                reservationId: prev.reservationId,
              });
            }
            // Not resumable any more (expired, dates passed…): book again below.
          }
        } else if (prev && prev.key !== key) {
          pendingPayment = null;
          if (readToken())
            void http
              .patch(`/api/reservations/${prev.reservationId}/abandon-payment`, { auth: 'required' })
              .catch(() => undefined);
        }

        const quote = await vehicleQuote(input.vehicleId, input.search).catch(() => undefined);
        // One event id per reservation: sent to the server (CAPI) and reused by the Purchase pixel.
        const ev = newEventId();
        const res = await http.raw('POST', '/api/reservations', {
          form: vehicleBookingForm(input, kind, quote?.total),
          headers: metaHeaders(ev),
        });
        if (res.status === 502 || res.status === 503) {
          const err = paymentFailure(
            new ApiError('', 'unavailable', { status: res.status, serverCode: (res.body as { code?: string })?.code }),
            res.body,
            key,
          );
          if (err.details.reservationId) pendingPayment = { key, reservationId: err.details.reservationId, eventId: ev };
          throw err;
        }
        if (res.status < 200 || res.status >= 300) throw bookingError(res.status, res.body);
        const parsed = CreateReservationSchema.safeParse(res.body);
        if (!parsed.success) throw new ApiError(MESSAGES.badResponse, 'unavailable', { status: res.status });
        const booking = await fromBooking(parsed.data.data, {
          vehicle,
          payment: toBookingPayment(parsed.data.payment),
        });
        booking.metaEventId = ev;
        created.set(booking.id, booking);
        rowKinds.set(booking.id, 'vehicle');
        if (booking.payment && !booking.payment.zeroFee) {
          pendingPayment = {
            key,
            reservationId: booking.id,
            eventId: ev,
            paymentIntentId: booking.payment.paymentIntentId,
            booking,
          };
        }
        return booking;
      },

      createExperience: async (input: ExperienceBookingInput) => {
        const sel = input.selection;
        assertSession();
        if (!user())
          throw new ApiError(tx('experienceBooking.account'), 'unauthorized', { status: 401 });
        const a = await getActivity(sel.experienceId);
        const key = experienceKey(input);
        const activityRow = (r: ReservationRow): ReservationRow => ({
          ...r,
          supplyType: 'activity',
          activity: { _id: a._id, title: a.title, city: a.city, images: a.images ?? [] },
        });

        // F8: the same checkout again → the unpaid reservation of this tab, never a second one.
        const prev = pendingExperience;
        if (prev && prev.key === key) {
          const resumed = await resumeExperience(prev.reservationId, input.currency, activityRow).catch(
            (e: unknown) => {
              if (e instanceof ApiError && e.code === 'unavailable') throw e;
              return null; // not resumable any more (swept, cancelled): book again below
            },
          );
          if (resumed) {
            pendingExperience = resumed.payment && !resumed.payment.zeroFee ? { ...prev, booking: resumed } : null;
            return resumed;
          }
          pendingExperience = null;
        } else if (prev) {
          // Another checkout: release the unpaid one first (an unpaid booking's cancel only frees its seats).
          pendingExperience = null;
          await http
            .patch(`/api/client/activity-reservations/${prev.reservationId}/cancel`, { auth: 'required' })
            .catch(() => undefined);
        }

        const slot = await slotFor(a, sel);
        const q = quoteBody(a, sel, slot);
        const c = input.contact;
        const kids = sel.children;
        const babies = sel.infants ?? 0;
        const head = sel.adults + kids + babies;
        const res = await http.raw('POST', '/api/client/activity-reservations', {
          auth: 'required',
          json: {
            activitySlotId: slot._id,
            selectedOptionId: q.optionId,
            participantCount: q.participantCount,
            eligibilityGrantId: sel.grantId || null,
            bookingAnswers: input.answers,
            vehicleAllocation: input.vehicleAllocation,
            physicalDetails: {
              ...(input.ages ? { ages: input.ages } : {}),
              ...(input.heightsCm ? { heightsCm: input.heightsCm } : {}),
              ...(input.weightsKg ? { weightsKg: input.weightsKg } : {}),
            },
            // AC-01c: the lead traveller named; real ages when the option asked for them (no invented age).
            participants: Array.from({ length: head }, (_, i) => ({
              name: i === 0 ? `${c.firstName} ${c.lastName}`.trim() : '',
              ...(input.ages?.[i] !== undefined ? { age: input.ages[i] } : {}),
              isChild: i >= sel.adults,
            })),
            selectedAddons: q.selectedAddons,
            ...(q.pickup ? { pickup: q.pickup } : {}),
            waiverAccepted: input.waiverAccepted,
            firstName: c.firstName.trim(),
            lastName: c.lastName.trim(),
            email: c.email.trim(),
            phoneNumber: c.phone.trim(),
            country: 'Maroc',
            city: a.city ?? '',
            // CUR-01: the card is charged in the chosen currency, converted by the server (Decision 45).
            currency: input.currency,
          },
        });
        if (res.status === 502 || res.status === 503) {
          const err = paymentFailure(
            new ApiError('', 'unavailable', { status: res.status, serverCode: (res.body as { code?: string })?.code }),
            res.body,
          );
          // The reservation exists: a retry resumes it (its intent) instead of booking twice.
          if (err.details.reservationId) pendingExperience = { key, reservationId: err.details.reservationId };
          throw err;
        }
        if (res.status < 200 || res.status >= 300) throw experienceBookingError(res.status, res.body);
        const parsed = CreateActivityReservationSchema.safeParse(res.body);
        if (!parsed.success) throw new ApiError(MESSAGES.badResponse, 'unavailable', { status: res.status });
        const booking = await fromBooking(activityRow(parsed.data.reservation), {
          agencyName: translate(ns.labels, 'verifiedProvider'),
          payment: toBookingPayment(parsed.data.payment),
        });
        booking.experienceId = a._id;
        created.set(booking.id, booking);
        rowKinds.set(booking.id, 'experience');
        pendingExperience =
          booking.payment && !booking.payment.zeroFee ? { key, reservationId: booking.id, booking } : null;
        return booking;
      },

      get: async (id) => {
        const local = created.get(id);
        if (!readToken()) {
          if (local) return local;
          throw new ApiError(tx('bookingSignIn'), 'unauthorized', { status: 401 });
        }
        const rows = await getReservations();
        const row = rows.find((r) => r._id === id);
        if (!row) {
          if (local) return local;
          throw notFound(tx('bookingNotFound'));
        }
        const names = await agencyNames([row]);
        const booking = await fromBooking(row, {
          agencyName: names.get(row.confirmedAgencyId ?? ''),
          payment: local?.payment,
          vehicle:
            local && local.kind === 'vehicle'
              ? { id: local.vehicleId ?? '', name: local.title, image: local.image, city: local.city }
              : undefined,
        });
        // P6: an experience's success page also needs its voucher QR payload and provider tickets (ACT-10).
        if (isActivityRow(row)) {
          const full = await getActivityReservation(id).catch(() => null);
          if (full) {
            const x = bookingExtras(full.extras);
            Object.assign(booking, {
              qrToken: x.qrToken,
              voucherPdf: x.voucherPdf,
              tickets: x.tickets,
              experienceId: x.experienceId,
              participants: booking.participants && { ...booking.participants, infants: x.infants },
            });
          }
        }
        return booking;
      },

      trips: async () => {
        const rows = (await getReservations()).filter((r) => !isCartRow(r) && r.status !== 'expired');
        const [agencies, ratings] = await Promise.all([
          agencyNames(rows),
          http.get('/api/ratings/client', { auth: 'required', schema: ClientRatingsSchema }).catch(() => []),
        ]);
        const rated = new Set(ratings.map((r) => idOf(r.carId)).filter(Boolean));
        return rows.map((r) => toTrip(r, { baseUrl, agencies, ratedVehicleIds: rated }));
      },

      carts: async () =>
        // Past-dated carts are listed too: the trips page shows them as «Dates passées» with only «Retirer» (AV-07).
        (await getReservations()).map((r) => toCart(r, baseUrl)).filter((c): c is NonNullable<typeof c> => !!c),

      cancel: async (tripId, reason = 'other') => {
        if (!rowKinds.has(tripId)) await getReservations();
        if (rowKinds.get(tripId) === 'experience') {
          await http.patch(`/api/client/activity-reservations/${tripId}/cancel`, {
            auth: 'required',
            messages: { 409: tx('cancelClosed') },
          });
          return;
        }
        await http.patch(`/api/reservations/${tripId}/client-cancel`, {
          auth: 'required',
          json: { reason },
          messages: { 404: tx('bookingNotFound') },
        });
      },

      removeCart: async (cartId) => {
        await http.patch(`/api/reservations/${cartId}/hide`, { auth: 'required' });
      },

      rate: async (tripId, stars, comment) => {
        if (!rowKinds.has(tripId)) await getReservations();
        // i18n-ignore-next-line: review text stored by the server and read by the agency (a value, Decision 98)
        const text = comment?.trim() || `Note globale : ${stars}/5`;
        if (rowKinds.get(tripId) === 'experience') {
          await http.post(`/api/client/activity-reservations/${tripId}/rating`, {
            auth: 'required',
            json: { knowledge: stars, fun: stars, value: stars, communication: stars, accuracy: stars, comment: text },
            messages: { 409: tx('rating.alreadyRated') },
          });
          return;
        }
        const carId = rowVehicle.get(tripId);
        if (!carId) throw notFound(tx('bookingNotFound'));
        // One score for the five criteria the vehicle form shows; the backend requires a comment.
        await http.post(`/api/cars/${carId}/ratings`, {
          auth: 'required',
          json: {
            service: stars,
            carCondition: stars,
            valueForMoney: stars,
            carPerformance: stars,
            cleanliness: stars,
            comment: text,
          },
        });
      },
    },

    favourites: {
      list: async () => {
        const cfg = await getConfigRaw().catch(() => null);
        const [vehicleDocs, activityDocs] = await Promise.all([
          http.get('/api/wishlist', {
            auth: 'required',
            schema: z.array(VehicleRecordSchema),
            messages: { unauthorized: tx('favourites.signIn') },
          }),
          activitiesBuildFlag && cfg?.activitiesEnabled !== false
            ? http.get('/api/activity-wishlist', { auth: 'required', schema: ActivityListSchema }).catch(() => [])
            : Promise.resolve([] as ActivityRecord[]),
        ]);
        const names = await categoryNames();
        // Wishlist rows are raw documents (no matrix price, bike brands as ids): read each model's public card.
        const vehicles = await Promise.all(
          vehicleDocs.map(async (doc) => {
            remember(doc);
            const raw = await getVehicleRaw(doc._id).catch(() => doc);
            return toVehicleSummary(raw, baseUrl, names);
          }),
        );
        for (const a of activityDocs) activityIds.add(a._id);
        return { vehicles, experiences: activityDocs.map((a) => toExperienceSummary(a, baseUrl)) };
      },
      ids: async () => {
        if (!readToken()) return [];
        const [vehicles, acts] = await Promise.all([
          http.get('/api/wishlist', { auth: 'required', schema: z.array(z.object({ _id: z.string() }).passthrough()) }),
          activitiesBuildFlag
            ? http
                .get('/api/activity-wishlist', {
                  auth: 'required',
                  schema: z.array(z.object({ _id: z.string() }).passthrough()),
                })
                .catch(() => [])
            : Promise.resolve([]),
        ]);
        for (const a of acts) activityIds.add(a._id);
        return [...vehicles.map((v) => v._id), ...acts.map((a) => a._id)];
      },
      toggle: async (id) => {
        if (!readToken())
          throw new ApiError(tx('favourites.save'), 'unauthorized', { status: 401 });
        const activity = activityIds.has(id);
        const base = activity ? '/api/activity-wishlist' : '/api/wishlist';
        const current = await http.get(base, {
          auth: 'required',
          schema: z.array(z.object({ _id: z.string() }).passthrough()),
        });
        const on = current.some((x) => x._id === id);
        await http.request(on ? 'DELETE' : 'POST', `${base}/${id}`, {
          auth: 'required',
          messages: { not_found: tx('itemGone') },
        });
        return !on;
      },
    },

    auth: {
      session: async () => {
        const token = readToken();
        if (!token) {
          clearSession();
          return null;
        }
        try {
          const me = await http.get('/api/client/auth/me', { auth: 'required', schema: ClientSchema });
          saveSession(null, me);
          return toUser(me, baseUrl);
        } catch (e) {
          if (e instanceof ApiError && e.code === 'unauthorized') {
            clearSession();
            return null;
          }
          // Offline or server down: keep the stored session.
          const stored = readClient();
          return stored ? toUser(ClientSchema.parse(stored)) : null;
        }
      },
      login: async (email, password) => {
        try {
          const res = await http.post('/api/client/auth/login', {
            auth: 'none',
            json: { email: email.trim(), password },
            schema: AuthResponseSchema,
          });
          saveSession(res.token, res.client);
          return toUser(res.client);
        } catch (e) {
          if (e instanceof ApiError && authMessage(e.details.serverCode)) {
            throw new ApiError(authMessage(e.details.serverCode) ?? MESSAGES.invalid, 'unauthorized', e.details);
          }
          if (e instanceof ApiError && (e.code === 'unauthorized' || e.code === 'not_found' || e.code === 'invalid')) {
            throw new ApiError(AUTH_MESSAGES.INVALID_CREDENTIALS, 'unauthorized', e.details);
          }
          throw e;
        }
      },
      register: async ({ name, email, password, marketing }) => {
        try {
          const res = await http.post('/api/client/auth/register', {
            auth: 'none',
            json: { clientname: name.trim(), email: email.trim(), password, marketingConsent: marketing },
            schema: AuthResponseSchema,
          });
          saveSession(res.token, res.client);
          return toUser(res.client);
        } catch (e) {
          if (e instanceof ApiError && authMessage(e.details.serverCode)) {
            throw new ApiError(authMessage(e.details.serverCode) ?? MESSAGES.invalid, 'invalid', e.details);
          }
          throw e;
        }
      },
      google: async (credential) => {
        // No credential: Google Identity Services is not configured or did not load (P7).
        if (!credential) {
          throw new ApiError(
            tx('auth.googleUnavailable'),
            'unavailable',
          );
        }
        try {
          const res = await http.post('/api/client/auth/google', {
            auth: 'none',
            json: { credential },
            schema: AuthResponseSchema,
          });
          saveSession(res.token, res.client);
          return toUser(res.client);
        } catch (e) {
          if (!(e instanceof ApiError)) throw e;
          const known = authMessage(e.details.serverCode);
          if (known) throw new ApiError(known, e.code, e.details);
          // 401 without a code is still a refused Google token; 5xx keeps the generic «indisponible».
          if (e.details.status === 401) throw new ApiError(AUTH_MESSAGES.INVALID_GOOGLE_TOKEN, 'unauthorized', e.details);
          if (e.details.status === 409) throw new ApiError(AUTH_MESSAGES.ACCOUNT_CONFLICT, 'invalid', e.details);
          throw e;
        }
      },
      logout: async () => {
        signOut(); // L1, L2 and L3 (STORAGE §2); nothing counts as expired after a sign-out
        pendingPayment = null;
        created.clear();
        threadsCache = { conversations: new Map(), messages: new Map() };
      },
      forgotPassword: async (email) => {
        try {
          const res = await http.post('/api/client/auth/forgot-password', {
            auth: 'none',
            json: { email: email.trim().toLowerCase() },
            schema: ForgotPasswordSchema,
          });
          // The reset needs the account id the server returns here (the e-mail only carries a code).
          if (res.clientId) writeSession('reset', { email: email.trim().toLowerCase(), clientId: res.clientId });
        } catch (e) {
          // No account: same answer as for an existing one (no account enumeration).
          if (e instanceof ApiError && e.code === 'not_found') return;
          if (e instanceof ApiError && e.details.serverCode === 'GOOGLE_AUTH_ACCOUNT') {
            throw new ApiError(AUTH_MESSAGES.GOOGLE_AUTH_ACCOUNT, 'invalid', e.details);
          }
          throw e;
        }
      },
      resetPassword: async (code, password) => {
        const pending = readSession<{ clientId?: string } | null>('reset', null);
        if (!pending?.clientId) {
          throw new ApiError(tx('auth.resetNoCode'), 'invalid');
        }
        try {
          await http.post('/api/client/auth/reset-password', {
            auth: 'none',
            json: { clientId: pending.clientId, code: code.trim(), password },
          });
        } catch (e) {
          // The backend answers 404 for a wrong or expired code.
          if (e instanceof ApiError && e.details.status === 404) {
            throw new ApiError(tx('auth.resetBadCode'), 'invalid', e.details);
          }
          throw e;
        }
        writeSession('reset', null);
      },
    },

    profile: {
      stats: async () => {
        const [rows, ids] = await Promise.all([getReservations(), readToken() ? apiFavIds() : Promise.resolve([])]);
        return { trips: rows.filter((r) => !isCartRow(r) && r.status !== 'expired').length, favourites: ids.length };
      },
      update: async ({ name, phone, dateOfBirth, bio, picture, removePicture }) => {
        const fields: Record<string, string> = {};
        if (name !== undefined) fields.clientname = name.trim();
        if (phone !== undefined) fields.phoneNumber = phone.trim();
        if (dateOfBirth !== undefined) fields.dateOfBirth = dateOfBirth;
        if (bio !== undefined) fields.bio = bio.trim();
        if (removePicture) fields.removeProfilePicture = 'true';
        // Multipart only with a photo (API-AUTH-8); the same fields as JSON otherwise.
        let form: FormData | undefined;
        if (picture && !removePicture) {
          form = new FormData();
          for (const [k, v] of Object.entries(fields)) form.append(k, v);
          form.append('profilePicture', picture);
        }
        try {
          const res = await http.post('/api/client/auth/profile/update', {
            auth: 'required',
            ...(form ? { form } : { json: fields }),
            schema: ProfileUpdateSchema,
            timeoutMs: form ? 60_000 : undefined,
          });
          saveSession(null, res.client);
          return toUser(res.client, baseUrl);
        } catch (e) {
          if (e instanceof ApiError && authMessage(e.details.serverCode)) {
            throw new ApiError(authMessage(e.details.serverCode) ?? MESSAGES.invalid, 'invalid', e.details);
          }
          throw e;
        }
      },
    },

    messages: {
      threads: async () => {
        const me = user();
        if (!me) throw new ApiError(tx('messages.signIn'), 'unauthorized', { status: 401 });
        const list = await http.get('/api/conversations/my', {
          auth: 'required',
          query: { channel: 'all' },
          schema: ConversationListSchema,
        });
        // One support row: the open conversation, else the latest resolved one (rating and «Nouvelle
        // conversation»), else a draft that its first message creates (CHT-1/2, P8). Listing never creates;
        // archived conversations are not shown (as on the current site).
        const supports = list.filter((c) => c.channel !== 'agency');
        const support = supports.find(isLiveSupport) ?? supports.find((c) => c.status === 'resolved');
        const shown = [...list.filter((c) => c.channel === 'agency'), ...(support ? [support] : [])];
        const pages = await Promise.all(
          shown.map((c) => getMessagePage(c._id).catch(() => ({ messages: [] as MessageRecord[], hasMore: false }))),
        );
        threadsCache = {
          conversations: new Map(shown.map((c) => [c._id, c])),
          messages: new Map(shown.map((c, i) => [c._id, pages[i]?.messages ?? []])),
        };
        const threads = shown
          .map((c, i) => ({
            thread: toThread(c, pages[i]?.messages ?? [], me.id, { hasMore: !!pages[i]?.hasMore }),
            at: pages[i]?.messages.at(-1)?.createdAt ?? c.updatedAt ?? '',
          }))
          .sort((a, b) => b.at.localeCompare(a.at))
          .map((x) => x.thread);
        return support ? threads : [...threads, supportDraftThread()];
      },
      send: async (threadId, text) => {
        let id = threadId;
        let created = false;
        if (id === SUPPORT_DRAFT_ID) {
          // First message of the support thread: find-or-create it (the server's bot greets on creation).
          const conv = await http.post('/api/conversations', { auth: 'required', schema: ConversationSchema });
          id = conv._id;
          created = true;
        }
        const cached = threadsCache.conversations.get(id);
        // Support state moves on the server (bot steps, agent, resolution): read it fresh before deciding.
        const conv = cached?.channel === 'agency' ? cached : await getConversation(id);
        if (conv.channel !== 'agency' && conv.status === 'bot_flow') {
          const msgs = await getMessages(id);
          const button = pendingBotButtons(conv, msgs).find((x) => x.label === text);
          if (button) {
            // A bot choice: answered with the button, shown as the client's message.
            await botClick(id, button.id);
            return {
              id: `choice-${button.id}-${Date.now()}`,
              from: 'me',
              text,
              time: toMessage({ _id: '', content: text, createdAt: new Date().toISOString() }).time,
              date: toISODate(new Date()),
              ...(created || id !== threadId ? { threadId: id } : {}),
            };
          }
          // Free text means «a person, please»: the bot's own path to an agent, then the message.
          await handOffToAgent(id);
        }
        const m = await http.post(`/api/conversations/${id}/messages`, {
          auth: 'required',
          json: { content: text },
          schema: MessageSchema,
          messages: {
            403: tx('messages.closed'),
          },
        });
        return { ...toMessage(m), ...(created ? { threadId: id } : {}) };
      },
      markRead: async (threadId) => {
        if (threadId === SUPPORT_DRAFT_ID) return;
        // The read cursor is the latest message (live messages may be newer than the last listing).
        const last = (await getMessages(threadId)).at(-1);
        if (!last) return;
        await http.post(`/api/conversations/${threadId}/read`, { auth: 'required', json: { messageId: last._id } });
      },
    },

    chat: {
      older: async (threadId, beforeId) => {
        const page = await getMessagePage(threadId, beforeId);
        return {
          messages: toMessages(page.messages),
          hasMore: !!page.hasMore,
        };
      },
      rate: async (threadId, rating) => {
        await http.patch(`/api/conversations/${threadId}/rate`, {
          auth: 'required',
          json: { rating },
          messages: { 400: tx('messages.rated') },
        });
      },
    },

    marketing: {
      accept: async () => {
        await http.patch('/api/client/auth/marketing-consent', { auth: 'required', json: { marketingConsent: true } });
        saveSession(null, { marketingConsent: true });
      },
    },

    /* ── Experience booking (P6): free-entry requests (ACT-8/9) and provider tickets (ACT-15) ── */
    experienceBooking: {
      requestEligibility: async (input) => {
        const g = await http.post('/api/client/activity-eligibility', {
          auth: 'required',
          json: {
            activityId: input.experienceId,
            optionId: input.packId,
            date: input.date,
            participantCount: input.participants,
            freeCount: input.free,
            proofReference: input.proof.trim(),
          },
          schema: EligibilitySchema,
          messages: {
            unauthorized: tx('eligibility.signIn'),
            429: tx('eligibility.tooMany'),
          },
        });
        return toEligibility(g);
      },
      eligibility: async (id) =>
        toEligibility(
          await http.get(`/api/client/activity-eligibility/${encodeURIComponent(id)}`, {
            auth: 'required',
            schema: EligibilitySchema,
            messages: { 404: tx('eligibility.notFound') },
          }),
        ),
      resendTickets: async (bookingId) => {
        await http.post(`/api/client/activity-reservations/${bookingId}/provider-tickets/resend`, {
          auth: 'required',
          messages: {
            404: tx('bookingNotFound'),
            409: tx('tickets.notIssued'),
            429: tx('tickets.wait'),
            502: tx('tickets.failed'),
          },
        });
      },
    },

    /* ── Cross-sell (API-ACT-16): experiences of a stay's city and days ── */
    crossSell: {
      experiences: async ({ city, from, to, limit }) => {
        const name = city.trim();
        if (!activitiesBuildFlag || !name) return [];
        const cfg = await getConfigRaw().catch(() => null);
        if (cfg?.activitiesEnabled === false) return [];
        // Slot days are UTC days (`slotDay`): ask for the whole of the first and the last day.
        const list = await http
          .get('/api/client/cross-sell/activities', {
            auth: 'none',
            query: {
              city: name,
              from: from ? `${from}T00:00:00.000Z` : undefined,
              to: to ? `${to}T23:59:59.999Z` : undefined,
              limit: limit ? Math.min(12, Math.max(1, limit)) : undefined,
            },
            schema: ActivityListSchema,
          })
          // The backend's kill switch answers 404 (the vertical «does not exist»): no rail.
          .catch((e: unknown) => {
            if (e instanceof ApiError && e.code === 'not_found') return [] as ActivityRecord[];
            throw e;
          });
        // The heart toggles the activity wishlist. The records are not cached for the detail page: this endpoint
        // does not populate `categoryId` (an id only), so the category is named from the categories list.
        for (const a of list) activityIds.add(a._id);
        const names = new Map(
          (list.some((a) => typeof a.categoryId === 'string')
            ? await getCategories().catch(() => [] as CategoryRecord[])
            : []
          ).map((c) => [c._id, c.name] as const),
        );
        return list.map((a) => {
          const id = typeof a.categoryId === 'string' ? a.categoryId : '';
          const named = names.get(id);
          return toExperienceSummary(named ? { ...a, categoryId: { _id: id, name: named } } : a, baseUrl);
        });
      },
    },

    partner: {
      submit: async (lead) => {
        const type = AGENCY_TYPE[lead.type];
        if (!type) {
          throw new ApiError(
            tx('partner.experiences'),
            'invalid',
          );
        }
        await http.post('/api/agencyRequests', {
          auth: 'required',
          form: partnerLeadForm(lead, type),
          messages: {
            unauthorized: tx('partner.signIn'),
            400: tx('partner.invalid'),
            409: tx('partner.exists'),
            500: tx('partner.failed'),
          },
        });
      },
    },

    newsletter: {
      subscribe: async (email) => {
        // `/api/newsletter/add` (emailController.addEmail) answers 500 for both a duplicate and an
        // address its own pattern refuses: told apart from the message.
        const res = await http.raw('POST', '/api/newsletter/add', { auth: 'none', json: { email: email.trim() } });
        if (res.status >= 200 && res.status < 300) return;
        const msg = String(
          (res.body as { message?: unknown; error?: unknown } | null)?.message ??
            (res.body as { error?: unknown } | null)?.error ??
            '',
        );
        // Already subscribed: the visitor's wish is fulfilled.
        if (/duplicate|already/i.test(msg)) return;
        if (/valid email/i.test(msg))
          throw new ApiError(tx('newsletter.email'), 'invalid', {
            status: res.status,
          });
        throw new ApiError(MESSAGES.unavailable, 'unavailable', { status: res.status });
      },
    },

    /*
     * ── Contact form (P14): `POST /api/send-email` (contactController.sendEmail, no auth, no rate limit).
     * 200 `{message:'Message reçu…'}` comes before the e-mails are queued (their failure is only logged);
     * 400 `{message:'Champs requis manquants.'}` when a field is empty. ──
     */
    contact: {
      send: async (message) => {
        await http.post('/api/send-email', {
          auth: 'none',
          json: contactBody(message),
          messages: {
            400: tx('contact.missing'),
            unavailable: tx('contact.failed'),
            not_found: tx('contact.failed'),
            network: tx('contact.network'),
          },
        });
      },
    },

    /* ── Payments (P4) ── */
    payments: {
      config: async () =>
        toPaymentConfig(
          await http.get('/api/payments/config', {
            auth: 'none',
            schema: PaymentConfigSchema,
            messages: { unavailable: tx('payments.unavailable') },
          }),
        ),
      finalize: async (paymentIntentId) => {
        // Stripe accepted the card: this tab's reservation is no longer unpaid (never abandon it afterwards).
        if (pendingPayment?.paymentIntentId === paymentIntentId) pendingPayment = null;
        if (pendingExperience?.booking?.payment?.paymentIntentId === paymentIntentId) pendingExperience = null;
        const r = await http.post('/api/payments/finalize', {
          // The route is public (`back/routes/paymentRoutes.js:35`): a card already authorised is always
          // finalized, even if the session expired meanwhile (the expiry rules apply to calls made as the user).
          auth: 'none',
          json: { paymentIntentId },
          schema: FinalizeSchema,
          messages: {
            unavailable: tx('payments.finalizing'),
          },
        });
        return toFinalizeResult(r);
      },
      resume: async (reservationId, currency) => {
        try {
          const pay = await http.patch(`/api/reservations/${reservationId}/resume-payment`, {
            auth: 'required',
            json: { currency },
            schema: ResumePaymentSchema,
            messages: { unauthorized: tx('payments.signIn'), 404: tx('bookingNotFound') },
          });
          const payment = toBookingPayment(pay);
          if (!payment || (!payment.zeroFee && !payment.clientSecret)) {
            throw new ApiError(MESSAGES.badResponse, 'unavailable', { serverCode: 'BAD_RESPONSE', reservationId });
          }
          return payment;
        } catch (e) {
          const code = e instanceof ApiError ? e.details.serverCode : undefined;
          if (e instanceof ApiError && code && RESUME_MESSAGES[code]) {
            throw new ApiError(RESUME_MESSAGES[code], code === 'RATE_UNAVAILABLE' ? 'unavailable' : 'invalid', {
              ...e.details,
              reservationId,
            });
          }
          if (e instanceof ApiError && e.details.status === 502) {
            throw new ApiError(
              tx('payments.init'),
              'unavailable',
              { ...e.details, reservationId },
            );
          }
          throw e;
        }
      },
    },

    /* ── Coupons (P3) ── */
    coupons: {
      check: async (code) => {
        const c = code.trim().toUpperCase();
        if (!c) throw new ApiError(COUPON_MESSAGES.empty, 'invalid');
        try {
          return toCouponCheck(
            await http.get('/api/coupons/check', { auth: 'none', query: { code: c }, schema: CouponCheckSchema }),
          );
        } catch (e) {
          if (e instanceof ApiError && e.details.status === 404)
            throw new ApiError(COUPON_MESSAGES.notFound, 'invalid', e.details);
          if (e instanceof ApiError && e.details.status === 400)
            throw new ApiError(COUPON_MESSAGES.limit, 'invalid', e.details);
          throw e;
        }
      },
    },

    /* ── Trip actions (P5) ── */
    trips: {
      cancelPreview: async (tripId) => {
        if ((await rowKind(tripId)) === 'experience')
          throw new ApiError(tx('cancel.experienceTerms'), 'invalid');
        const body = await http.get(`/api/reservations/${tripId}/cancel-preview`, {
          auth: 'required',
          schema: CancelPreviewSchema,
          messages: { 404: tx('bookingNotFound') },
        });
        return toCancelPreview(body);
      },
      cancel: async (tripId, request) => {
        if ((await rowKind(tripId)) === 'experience') {
          const res = await http.patch(`/api/client/activity-reservations/${tripId}/cancel`, {
            auth: 'required',
            schema: ActivityCancelSchema,
            messages: { 404: tx('bookingNotFound'), 409: tx('cancelClosed') },
          });
          return toCancelOutcome(res.refund);
        }
        const res = await http.patch(`/api/reservations/${tripId}/client-cancel`, {
          auth: 'required',
          json: clientCancelBody(request),
          schema: VehicleCancelSchema,
          messages: { 404: tx('bookingNotFound') },
        });
        if (res.success === false) throw new ApiError(showableServerText(res.message) ?? MESSAGES.invalid, 'invalid');
        return toCancelOutcome(res.refund, res.data?.payment?.currency ?? 'MAD');
      },
      requestExtension: async (tripId, request) => {
        const res = await http.post(`/api/reservations/${tripId}/request-extension`, {
          auth: 'required',
          // The local day as picked, never through toISOString (D20).
          json: {
            newReturnDate: request.returnDate,
            newReturnTime: request.returnTime,
            reason: request.reason?.trim() ?? '',
          },
          schema: ExtensionResponseSchema,
          messages: {
            404: tx('bookingNotFound'),
            409: tx('extension.unavailable'),
          },
        });
        return toExtension(res, request);
      },
      rate: async (tripId, rating) => {
        const comment = rating.comment.trim();
        if (!comment) throw new ApiError(tx('rating.comment'), 'invalid');
        const sc = rating.scores;
        if ((await rowKind(tripId)) === 'experience') {
          await http.post(`/api/client/activity-reservations/${tripId}/rating`, {
            auth: 'required',
            json: {
              knowledge: sc.knowledge,
              fun: sc.fun,
              value: sc.value,
              communication: sc.communication,
              accuracy: sc.accuracy,
              comment,
            },
            messages: { 409: tx('rating.notYet') },
          });
          return;
        }
        const carId = rowVehicle.get(tripId);
        if (!carId) throw notFound(tx('bookingNotFound'));
        await http.post(`/api/cars/${carId}/ratings`, {
          auth: 'required',
          json: {
            service: sc.service,
            carCondition: sc.carCondition,
            valueForMoney: sc.valueForMoney,
            carPerformance: sc.carPerformance,
            cleanliness: sc.cleanliness,
            comment,
          },
        });
      },
      voucherPdf: async (tripId) => {
        const rows = await getReservations();
        const row = rows.find((r) => r._id === tripId);
        if (!row) throw notFound(tx('bookingNotFound'));
        const activity = isActivityRow(row);
        const code = row.voucher?.code || fallbackRef(row._id);
        const blob = await downloadBlob(
          activity
            ? `/api/client/activity-reservations/${tripId}/voucher.pdf`
            : `/api/reservations/${tripId}/voucher.pdf`,
        );
        return { blob, fileName: activity ? `cardnd-voucher-${code}.pdf` : `bon-reservation-${code}.pdf` };
      },
      toRate: async () => {
        if (!readToken()) return null;
        // Legacy home check (STA-5 → RAT-7): the first completed reservation whose return is past,
        // offered only when its vehicle is not rated yet.
        const rows = await getReservations();
        const now = Date.now();
        const done = rows.find(
          (r) => !isActivityRow(r) && r.status === 'completed' && !!r.returnDate && Date.parse(r.returnDate) < now,
        );
        const carId = done ? rowVehicle.get(done._id) : undefined;
        if (!done || !carId) return null;
        const ratings = await http.get('/api/ratings/client', { auth: 'required', schema: ClientRatingsSchema });
        if (ratings.some((x) => idOf(x.carId) === carId)) return null;
        return toTrip(done, { baseUrl, agencies: await agencyNames([done]), ratedVehicleIds: new Set() });
      },
    },

    /* ── Account (P13) ── */
    reviews: {
      list: async () =>
        (
          await http.get('/api/ratings/client', {
            auth: 'required',
            schema: ClientReviewListSchema,
            messages: { unauthorized: tx('reviews.signIn'), unavailable: tx('reviews.load') },
          })
        ).map((r) => toReview(r, baseUrl)),
      remove: async (reviewId) => {
        await http.delete(`/api/ratings/${reviewId}`, {
          auth: 'required',
          messages: { 403: tx('reviews.notYours'), 404: tx('reviews.gone') },
        });
      },
    },
    account: {
      partnerStatus: async () =>
        toPartnerStatus(await http.get('/api/client/auth/partner/status', { auth: 'required', schema: PartnerStatusSchema })),
    },

    /* ── Results (P15) ── */
    results: {
      page: async (search, filters, page) => {
        if (search.kind === 'moto') {
          // `bikes/available` has no paging: the whole list is one page.
          return { ...(await searchVehicles(search, filters)), page: 1, totalPages: 1 };
        }
        const { sortBy, order } = SORT[filters.sort] ?? SORT['price-asc'];
        const [names, res] = await Promise.all([
          categoryNames(),
          http.get('/api/client/available-cars', {
            auth: 'none',
            query: {
              city: search.city,
              startDate: search.start,
              returnDate: search.end,
              pickupTime: search.pickupTime,
              returnTime: search.returnTime,
              sortBy,
              order,
              page,
              limit: resultsPageSize,
              category: filters.category !== ALL_CATEGORIES ? filters.category : undefined,
              transmission: filters.transmission !== 'all' ? TRANSMISSION_PARAM[filters.transmission] : undefined,
              freeDelivery: filters.freeDelivery ? 'true' : undefined,
            },
            schema: AvailableCarsSchema,
          }),
        ]);
        // The server filters, sorts and pages (one card per model); its order is kept across pages.
        return {
          items: res.cars.map(remember).map((r) => toVehicleSummary(r, baseUrl, names)),
          page,
          totalPages: Math.max(1, Number(res.totalPages) || 1),
          // API-VEH-5: a platform blackout on these dates (no car, `totalPages: 0`) has its own empty state.
          blackout: res.blackout === true,
        };
      },
      activityCategory: async (id) => {
        const list = await getCategories().catch(() => [] as CategoryRecord[]);
        return list.find((c) => c._id === id)?.name ?? null;
      },
    },
  };

  /** Kind of a reservation of the client (read once from the list when unknown). */
  async function rowKind(id: string): Promise<'vehicle' | 'experience'> {
    if (!rowKinds.has(id)) await getReservations();
    const kind = rowKinds.get(id);
    if (!kind) throw notFound(tx('bookingNotFound'));
    return kind;
  }

  /** A file of the API (voucher PDF): the JSON client reads text, so this one fetch reads the bytes. */
  async function downloadBlob(path: string): Promise<Blob> {
    // Through the client (AU-02): an expired token or a 401 ends the session like any authed call.
    const res = await http.blob(path, {
      auth: 'required',
      headers: { Accept: 'application/pdf, */*' },
      messages: { unauthorized: tx('voucher.signIn') },
      timeoutMs: 60_000,
    });
    if (res.status === 409)
      throw new ApiError(tx('voucher.notReady'), 'invalid', { status: 409 });
    if (!res.blob)
      throw new ApiError(
        res.status === 404 ? tx('bookingNotFound') : tx('voucher.failed'),
        res.status === 401 || res.status === 403 ? 'unauthorized' : res.status === 404 ? 'not_found' : 'unavailable',
        { status: res.status },
      );
    return res.blob;
  }

  async function apiFavIds(): Promise<string[]> {
    const vehicles = await http.get('/api/wishlist', {
      auth: 'required',
      schema: z.array(z.object({ _id: z.string() }).passthrough()),
    });
    const acts = activitiesBuildFlag
      ? await http
          .get('/api/activity-wishlist', {
            auth: 'required',
            schema: z.array(z.object({ _id: z.string() }).passthrough()),
          })
          .catch(() => [])
      : [];
    return [...vehicles.map((v) => v._id), ...acts.map((a) => a._id)];
  }
}
